import React from 'react';
import {
  Box,
  Chip,
  Collapse,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import {
  Cancel,
  CheckCircle,
  ExpandLess,
  ExpandMore,
} from '@mui/icons-material';
import type { AuditReportGamesTableProps } from '../../../../types/auditReportDialogTypes';
import {
  formatAuditReportCurrency,
  formatAuditReportNumber,
} from '../utils/auditReportDialogUtils';

const AuditReportGamesTable: React.FC<AuditReportGamesTableProps> = React.memo(({
  games,
  expandedGames,
  onToggleGame,
}) => {
  if (games.length === 0) {
    return null;
  }

  return (
    <Paper elevation={1} sx={{ p: 2, mb: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
          Games ({games.length})
        </Typography>
      </Box>

      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold', width: '50px' }}></TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Game Name</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }} align="right">PLU</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }} align="right">Packs</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }} align="right">Qty Variance</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }} align="right">Revenue Variance</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }} align="center">Status</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {games.map((game) => {
              const quantityVariance = game.reconciliation_variance?.quantity_variance || 0;
              const revenueVariance = game.reconciliation_variance?.revenue_variance || 0;
              const matches = quantityVariance === 0 && revenueVariance === 0;
              const isExpanded = expandedGames.has(game.game_id);
              const packCount = game.packs?.length || 0;

              return (
                <React.Fragment key={game.game_id}>
                  <TableRow>
                    <TableCell>
                      {packCount > 0 && (
                        <IconButton
                          size="small"
                          onClick={() => onToggleGame(game.game_id)}
                          sx={{ p: 0.5 }}
                        >
                          {isExpanded ? <ExpandLess /> : <ExpandMore />}
                        </IconButton>
                      )}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 'medium' }}>{game.game_name}</TableCell>
                    <TableCell align="right">{game.plu}</TableCell>
                    <TableCell align="right">{packCount}</TableCell>
                    <TableCell
                      align="right"
                      sx={{
                        color: quantityVariance === 0 ? 'text.primary' : 'error.main',
                        fontWeight: quantityVariance !== 0 ? 'bold' : 'normal',
                      }}
                    >
                      {formatAuditReportNumber(quantityVariance)}
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{
                        color: revenueVariance === 0 ? 'text.primary' : 'error.main',
                        fontWeight: revenueVariance !== 0 ? 'bold' : 'normal',
                      }}
                    >
                      {formatAuditReportCurrency(revenueVariance)}
                    </TableCell>
                    <TableCell align="center">
                      {matches ? (
                        <Chip icon={<CheckCircle />} label="Match" color="success" size="small" />
                      ) : (
                        <Chip icon={<Cancel />} label="Mismatch" color="error" size="small" />
                      )}
                    </TableCell>
                  </TableRow>

                  {isExpanded && game.packs && game.packs.length > 0 && (
                    <TableRow>
                      <TableCell colSpan={7} sx={{ py: 0, borderBottom: 'none' }}>
                        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                          <Box sx={{ margin: 2 }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                              Packs ({packCount})
                            </Typography>

                            <Table size="small">
                              <TableHead>
                                <TableRow>
                                  <TableCell sx={{ fontWeight: 'bold' }}>Pack ID</TableCell>
                                  <TableCell sx={{ fontWeight: 'bold' }} align="right">Start Ticket</TableCell>
                                  <TableCell sx={{ fontWeight: 'bold' }} align="right">End Ticket</TableCell>
                                  <TableCell sx={{ fontWeight: 'bold' }} align="right">Qty Sold</TableCell>
                                  <TableCell sx={{ fontWeight: 'bold' }} align="center">Newly Activated</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {game.packs.map((pack) => (
                                  <TableRow key={pack.pack_id}>
                                    <TableCell>{pack.pack_id}</TableCell>
                                    <TableCell align="right">
                                      {formatAuditReportNumber(pack.starting_ticket_number)}
                                    </TableCell>
                                    <TableCell align="right">
                                      {formatAuditReportNumber(pack.ending_ticket_number)}
                                    </TableCell>
                                    <TableCell align="right">
                                      {formatAuditReportNumber(pack.qty_sold)}
                                    </TableCell>
                                    <TableCell align="center">
                                      {pack.is_newly_activated ? (
                                        <Chip label="Yes" color="success" size="small" />
                                      ) : (
                                        <Chip label="No" size="small" variant="outlined" />
                                      )}
                                    </TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  )}
                </React.Fragment>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
});

AuditReportGamesTable.displayName = 'AuditReportGamesTable';

export default AuditReportGamesTable;
